import { Button, Chip, Collapse, Stack, Typography } from "@mui/material"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorIds } from "./hooks/use-schedule-editor-ids"
import { useScheduleEditorActions } from "./hooks/use-schedule-editor-actions"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { CriticalityIcon } from "routes/schedule/plugins/legislation-resolutions/criticality-icon"
import { useAmendsValue } from "./hooks/use-amends-value"
import { SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION } from "./lib/schedule-editor-action-types"
import { restoreDraftSnapshotInPlace } from "./lib/restore-draft-snapshot"
import { confirmApplyAuthoringRecommendationIfDrifted } from "./lib/confirm-apply-drifted-authoring-recommendation"
import {
    applyAuthoringRecommendationDisplayItemToDraft,
    AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED,
    AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED,
    resolveAuthoringRecommendationDisplayItemApplyPayload,
    resolveMergedGroupDisplayState,
} from "./lib/authoring-recommendation-groups"
import { resolveAuthoringRecommendationDraft } from "./lib/authoring-schedule-recommendations"
import {
    canFocusAuthoringRecommendationTarget,
    focusAuthoringRecommendationTarget,
} from "./lib/focus-authoring-recommendation-target"
import { saveAuthoringRecommendationAmend } from "./lib/save-authoring-recommendation-amend"
import {
    collectProjectBridgeAppliedTargets,
    persistProjectBridgeAppliedTargets,
} from "./lib/project-bridge-applied-recommendations"
import { buildAuthoringRecommendationStateMeta } from "./lib/authoring-recommendation-state-presenters"
import { getAuthoringRecommendationRouteToCurrentShell } from "./lib/authoring-recommendation-route"
import { AuthoringRecommendationGroupSourceRow } from "./authoring-recommendation-group-source-row"
import { SourceBadgeChip } from "./source-badge-chip"
import { useScheduleEditorDraftStructureVersion } from "./hooks/use-schedule-editor-draft-structure-version"
import { useScheduleEditorDraftEditedVersion } from "./hooks/use-schedule-editor-draft-edited-version"

export function AuthoringRecommendationGroupRow({
    item,
    selectedRecommendationId,
    onSelectRecommendation,
    onHoverRecommendation,
    onLeaveRecommendation,
    selectedRecommendationIdsByGroupKey,
    onSelectRecommendationGroupChoice,
}) {
    const navigate = useNavigate()
    const { target, rawSchedule } = useBoundContext()
    const { projectId, scheduleId, draftId } = useScheduleEditorIds()
    const { pushUndo, saveAmend, notifyDraftStructureChanged } = useScheduleEditorActions()
    const { amends: authoringAmends } = useAmendsValue()
    useScheduleEditorDraftStructureVersion()
    useScheduleEditorDraftEditedVersion()
    const draft = resolveAuthoringRecommendationDraft({ target, rawSchedule })
    const [showContributors, setShowContributors] = useState(false)
    const previewRecommendation = item?.previewRecommendation || item?.items?.[0] || null
    const canFocusRecommendation = canFocusAuthoringRecommendationTarget({
        recommendation: previewRecommendation,
        draft: target,
    })
    const isSelected = Boolean(item?.id) && item.id === selectedRecommendationId
    const groupState = resolveMergedGroupDisplayState({
        item,
        draft,
        selectedRecommendationIdsByGroupKey,
    })
    const stateMeta = buildAuthoringRecommendationStateMeta(groupState)
    const selectedChoiceId =
        item?.selectedRecommendationId || selectedRecommendationIdsByGroupKey?.[item?.groupKey] || ""
    const sourceCount = item?.sourceCount || item?.sourceBadges?.length || 1
    const applyDisabled = buildApplyDisabled()

    return (
        <ListItemBox
            onClick={handleSelect}
            onMouseEnter={handleHover}
            onMouseLeave={handleLeave}
            sx={buildRowStyles(isSelected)}
            data-cy={`authoring-recommendation-group-row-${item?.id || "unknown"}`}
        >
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pt: 1, pb: 1.5, pl: 1.5, pr: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        {item?.changeLabel || "Recommendation"}
                    </Typography>
                    <Button size="small" color="primary" disabled={applyDisabled} onClick={handleApplyRecommendation}>
                        Apply
                    </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Repeat list={item?.sourceBadges || []} item={<SourceBadgeChip />} />
                    <Chip size="small" label={stateMeta.label} color={stateMeta.color} variant={stateMeta.variant} />
                    <If value={Boolean(item?.criticality)}>
                        <CriticalityIcon criticality={item?.criticality} />
                    </If>
                </Stack>
                <Typography variant="body2" color="text.primary">
                    {item?.mergeTargetLabel || item?.targetLabel || "Recommendation"}
                </Typography>
                <If value={(item?.mergeMode === "composable" || item?.mergeMode === "consensus") && sourceCount > 1}>
                    <Typography variant="caption" color="text.secondary">
                        Combined from {sourceCount} sources.
                    </Typography>
                </If>
                <If value={groupState === AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED}>
                    <Stack spacing={0.75}>
                        <Typography variant="caption" color="text.secondary">
                            Choose which version to apply in the combined view.
                        </Typography>
                        <Repeat
                            list={item?.items || []}
                            item={
                                <AuthoringRecommendationGroupSourceRow
                                    selectable
                                    selectedItemId={selectedChoiceId}
                                    onSelect={handleSelectChoice}
                                    onOpenSource={handleOpenSource}
                                />
                            }
                        />
                    </Stack>
                </If>
                <If value={sourceCount > 1}>
                    <Button size="small" sx={{ alignSelf: "flex-start" }} onClick={toggleContributors}>
                        {showContributors ? "Hide" : "Show"} contributing recommendations
                    </Button>
                    <Collapse in={showContributors}>
                        <Stack spacing={0.75}>
                            <Repeat
                                list={item?.items || []}
                                item={<AuthoringRecommendationGroupSourceRow onOpenSource={handleOpenSource} />}
                            />
                        </Stack>
                    </Collapse>
                </If>
            </Stack>
        </ListItemBox>
    )

    function buildApplyDisabled() {
        if (groupState === AUTHORING_RECOMMENDATION_GROUP_STATE_APPLIED) return true
        if (groupState === AUTHORING_RECOMMENDATION_GROUP_STATE_CHOICE_REQUIRED) {
            return !selectedChoiceId
        }

        return false
    }

    function handleSelect() {
        onSelectRecommendation?.(item)
        if (canFocusRecommendation) {
            showInEditor()
        }
    }

    function handleHover(event) {
        onHoverRecommendation?.(item, event.currentTarget)
    }

    function handleLeave() {
        onLeaveRecommendation?.()
    }

    function handleSelectChoice(recommendation) {
        onSelectRecommendationGroupChoice?.(item?.groupKey, recommendation?.id || "")
    }

    function handleOpenSource(recommendation) {
        const routeTo = getAuthoringRecommendationRouteToCurrentShell(recommendation)
        if (!routeTo) return
        navigate(routeTo)
    }

    async function handleApplyRecommendation(event) {
        event.stopPropagation()
        const workingDraft = rawSchedule ?? target
        const payload = resolveAuthoringRecommendationDisplayItemApplyPayload({
            item,
            selectedRecommendationIdsByGroupKey,
        })
        if (!payload.recommendation) {
            errorSnackbar("This recommendation group no longer matches the current draft")
            return
        }

        const { proceed, force } = await confirmApplyAuthoringRecommendationIfDrifted({
            recommendation: payload.recommendation,
            draft: workingDraft,
        })
        if (!proceed) return

        const snapshot = structuredClone(workingDraft)
        const result = applyAuthoringRecommendationDisplayItemToDraft({
            item,
            draft: workingDraft,
            selectedRecommendationIdsByGroupKey,
            force,
        })
        if (!result?.applied) {
            errorSnackbar("This recommendation group no longer matches the current draft")
            return
        }

        try {
            const amendUndo = await saveAuthoringRecommendationAmend({
                saveAmend,
                authoringAmends,
                projectId,
                scheduleId,
                amendsKey: result?.amendsKey,
                recommendationId: item?.id,
                sourceType: "merged",
                sourceRecordId: item?.groupKey,
                sourceLabel: "Merged",
                sourceRoute: null,
                noteRecommendations: result?.noteRecommendations,
            })
            const projectBridgeApplied = await persistGroupProjectBridgeApplied()
            if (pushUndo) {
                pushUndo({
                    type: SCHEDULE_EDITOR_ACTION_RESTORE_RECOMMENDATION_APPLICATION,
                    snapshot,
                    amendUndo,
                    projectBridgeApplied,
                })
            }
            notifyDraftStructureChanged?.()
            successSnackbar("Merged recommendation applied")
        } catch (error) {
            restoreDraftSnapshotInPlace(rawSchedule ?? target, snapshot)
            notifyDraftStructureChanged?.()
            console.error("Failed to apply merged recommendation", error)
            errorSnackbar("Failed to apply merged recommendation")
        }
    }

    async function persistGroupProjectBridgeApplied() {
        const { importId, targets } = collectProjectBridgeAppliedTargets(item?.items)
        if (targets.length === 0) return null

        return persistProjectBridgeAppliedTargets({ importId, targets, projectId, scheduleId, draftId, applied: true })
    }

    function showInEditor() {
        const focused = focusAuthoringRecommendationTarget({ recommendation: previewRecommendation, draft: target })
        if (!focused) {
            errorSnackbar("Couldn't find this item in the editor")
        }
    }

    function toggleContributors(event) {
        event.stopPropagation()
        setShowContributors((value) => !value)
    }
}

function buildRowStyles(isSelected) {
    return {
        alignItems: "flex-start",
        cursor: "pointer",
        position: "relative",
        backgroundColor: "transparent",
        borderRadius: 1,
        transition: "background-color 0.2s ease",
        "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: 5,
            backgroundColor: isSelected ? "primary.main" : "transparent",
            pointerEvents: "none",
        },
        "&:focus, &:focus-visible, &:active": {
            backgroundColor: "transparent",
        },
        "&:hover": {
            backgroundColor: isSelected ? "transparent" : "action.hover",
        },
    }
}
